import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import { formatCurrency } from '../../utils/currency';
import { reportService, SalesDataPoint } from '../../services/reportService';

export const RevenueWidget: React.FC = () => {
    const theme = useTheme();
    const [revenue, setRevenue] = useState<number>(0);
    const [margin, setMargin] = useState<number>(0);
    const [chartData, setChartData] = useState<any[]>([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                // Fetch Total Revenue, Sales Trend, and Unit Economics
                const [stats, sales, economics] = await Promise.all([
                    reportService.getDashboardStats(),
                    reportService.getSalesChart(),
                    reportService.getUnitEconomics()
                ]);
 
                setRevenue(stats.revenue?.value || 0);
                setMargin(economics.grossMargin || 0);
 
                // Transform sales data for chart
                const formattedChartData = sales.map((item: SalesDataPoint) => ({
                    time: item._id.substring(5), // Extract MM-DD
                    value: item.sales
                }));
                setChartData(formattedChartData);
            } catch (error) {
                console.error("Failed to load Revenue Widget data", error);
            }
        };
        loadData();
    }, []);

    const growth = (revenue > 0) ? 0 : 0; // Placeholder until we plumb separate state for widget, OR use the stats growth
    // Actually stats is local scope. Let's make stats stateful if we want growth.
    // For now, let's just fix the RsNaN. 
    // Wait, I can grab growth easily if I move stats to state or just grab it inside loadData.
    // Simple fix first.

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="div" gutterBottom>
                    Total Revenue
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2, gap: 2 }}>
                    <Typography variant="h4" component="span" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(revenue)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                color: margin >= 40 ? theme.palette.success.main : theme.palette.warning.main,
                                fontWeight: 'medium'
                            }}
                        >
                            {margin.toFixed(1)}% Margin
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData.length > 0 ? chartData : [{ time: '', value: 0 }]}
                            margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="time" hide />
                            <YAxis hide />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`
                                }}
                                formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                            />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke={theme.palette.primary.main}
                                fillOpacity={1}
                                fill="url(#colorRevenue)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};
